import styled, {css} from 'styled-components'

export const StyledComposeWrapper = styled.form`
    ${({theme}) => css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing * 5}px;
        padding: ${theme.spacing * 6}px;
    `}
`

export const StyledSectionHeaderRow = styled.div`
    ${({theme}) => css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: ${theme.spacing * 2}px;
        margin-bottom: ${theme.spacing * 2}px;

        & > label {
            ${theme.typography.textSm};
            font-weight: 500;
            color: ${theme.palette.neutral['700']};
        }

        & > small {
            ${theme.typography.textXs};
            color: ${theme.palette.neutral['400']};
        }
    `}
`

export const StyledRecipientsGrid = styled.div`
    ${({theme}) => css`
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: ${theme.spacing * 3}px;

        ${theme.mediaQueries.m} {
            grid-template-columns: 1fr;
        }
    `}
`

// Sits left of the header CTAs. Never shrinks/wraps to a second line — it
// keeps its natural content width instead of being squeezed by the flex row.
export const StyledHeaderBannerWrapper = styled.div`
    display: flex;
    flex-shrink: 0;
    white-space: nowrap;
`

export const StyledStatusSection = styled.section`
    ${({theme}) => css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing * 1.5}px;

        & > div.heading {
            display: flex;
            align-items: center;
            gap: ${theme.spacing * 2}px;
            flex-wrap: wrap;

            & > label {
                ${theme.typography.textSm};
                font-weight: 500;
                color: ${theme.palette.neutral['700']};
            }

            & > small {
                display: inline-flex;
                align-items: center;
                gap: ${theme.spacing}px;
                ${theme.typography.textXs};
                color: ${theme.palette.neutral['400']};

                & svg {
                    width: 12px;
                    height: 12px;
                }
            }
        }
    `}
`

export const StyledEmailContentCard = styled.section`
    ${({theme}) => css`
        border: 1px solid ${theme.palette.neutral['200']};
        border-radius: 8px;
        padding: ${theme.spacing * 4}px;
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing * 3}px;
    `}
`

export const StyledEmailContentHeader = styled.div`
    ${({theme}) => css`
        display: flex;
        align-items: center;
        gap: ${theme.spacing * 2}px;
        padding-bottom: ${theme.spacing * 2}px;
        border-bottom: 1px solid ${theme.palette.neutral['100']};

        & > svg {
            width: 16px;
            height: 16px;
            color: ${theme.palette.neutral['600']};
        }

        & > h3 {
            ${theme.typography.textSm};
            font-weight: 500;
            color: ${theme.palette.neutral['900']};
        }
    `}
`

export const StyledCharCounter = styled.div`
    ${({theme}) => css`
        display: flex;
        justify-content: flex-end;
        margin-top: ${theme.spacing}px;
        font-size: 10px;
        line-height: 12px;
        color: ${theme.palette.neutral['400']};
    `}
`

export const StyledDropzoneButton = styled.button`
    ${({theme}) => css`
        width: 100%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: ${theme.spacing * 2}px;
        padding: ${theme.spacing * 3}px;
        ${theme.typography.textSm};
        color: ${theme.palette.neutral['600']};
        background: transparent;
        border: 2px dashed ${theme.palette.neutral['200']};
        border-radius: 6px;
        cursor: pointer;
        transition: ${theme.transition};

        &:hover {
            border-color: ${theme.palette.primary['300']};
        }

        & svg {
            width: 16px;
            height: 16px;
        }
    `}
`

export const StyledAttachmentsList = styled.div`
    ${({theme}) => css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing}px;
        margin-top: ${theme.spacing * 2}px;
    `}
`

export const StyledAttachmentRow = styled.div`
    ${({theme}) => css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: ${theme.spacing * 2}px;
        padding: ${theme.spacing * 2}px ${theme.spacing * 3}px;
        background: ${theme.palette.neutral['50']};
        border: 1px solid ${theme.palette.neutral['100']};
        border-radius: 4px;

        & > div {
            display: flex;
            align-items: center;
            gap: ${theme.spacing * 2}px;
            min-width: 0;

            & svg {
                width: 14px;
                height: 14px;
                color: ${theme.palette.neutral['500']};
                flex-shrink: 0;
            }

            & span.name {
                ${theme.typography.textXs};
                color: ${theme.palette.neutral['900']};
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            & span.size {
                font-size: 10px;
                color: ${theme.palette.neutral['500']};
                flex-shrink: 0;
            }
        }
    `}
`

export const StyledAttachmentsTotal = styled.small`
    ${({theme}) => css`
        display: block;
        margin-top: ${theme.spacing * 2}px;
        ${theme.typography.textXs};
        color: ${theme.palette.neutral['500']};
    `}
`

// Wraps a relocated banner (into the page header, next to the send/test CTAs)
// so it can shrink/wrap instead of pushing the buttons out of view.
export const StyledHeaderBannerSlot = styled.div`
    flex: 1 1 auto;
    min-width: 0;
    max-width: 480px;
`

export const StyledActionsGroup = styled.div`
    ${({theme}) => css`
        display: flex;
        align-items: center;
        gap: ${theme.spacing * 2}px;
    `}
`

// Tooltip anchor for the locked send button: disabled buttons swallow hover
// events, so they are made pointer-transparent and the wrapper carries the
// tooltip trigger (and the help cursor).
export const StyledLockedAction = styled.span`
    display: inline-flex;
    cursor: help;

    & > button:disabled {
        pointer-events: none;
    }
`
